<!--
  Website Overview ("Nasze kliniki i instytuty")

  Three cards linking to KLNP, Family Clinic and Institute homepages.
  Headings and card copy use neutral dark text; cards are white with a gray
  border (Figma Home node 7660:6267–6284).
-->
<template>
  <section class="flex w-full flex-col items-center gap-12 overflow-hidden px-4 py-16 md:px-16">
    <div class="mx-auto flex w-full max-w-[1200px] flex-col gap-12">
      <div class="flex flex-col gap-1">
        <p
          v-if="overlineText"
          class="text-[14px] font-semibold uppercase leading-5 tracking-[1px]"
        >
          {{ overlineText }}
        </p>
        <h2
          class="text-[36px] font-medium leading-[44px] tracking-[-0.02em]"
        >
          {{ headingText }}
        </h2>
      </div>

      <div class="grid grid-cols-1 items-stretch gap-8 md:grid-cols-3">
        <article
          v-for="card in cards"
          :key="card.siteContext"
          class="relative flex h-full min-h-[220px] flex-col gap-5 overflow-hidden rounded-xl border border-[#e5e7eb] bg-white p-6"
          :style="{ backgroundImage: card.gradient }"
        >
          <div class="relative flex flex-1 flex-col gap-2">
            <h3 class="whitespace-pre-wrap text-[20px] font-semibold leading-7 tracking-[-0.02em]">
              {{ card.title }}
            </h3>
            <p
              v-if="card.description"
              class="text-[16px] font-normal leading-6"
            >
              {{ card.description }}
            </p>
          </div>

          <a
            v-if="card.buttonLabel"
            :href="card.buttonUrl || '#'"
            class="relative mt-auto inline-flex items-center justify-center gap-2 self-start rounded-lg px-5 py-[14px] text-[16px] font-medium transition-opacity hover:opacity-80"
          >
            {{ card.buttonLabel }}
            <svg
              class="size-5 shrink-0"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4.5 10H15.5M15.5 10L11 5.5M15.5 10L11 14.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const CARD_SITE_CONTEXTS = [
  'infertility_treatment_clinic',
  'family_clinic',
  'institute',
];

// Soft diagonal wash derived from each context's light "secondary" tint,
// fading into the white card so the brand context is reflected subtly.
const CARD_GRADIENTS = {
  infertility_treatment_clinic: 'linear-gradient(to bottom left, #DAE9F1 0%, #FFFFFF 55%)',
  family_clinic: 'linear-gradient(to bottom left, #D5F6F0 0%, #FFFFFF 55%)',
  institute: 'linear-gradient(to bottom left, #D5EEF6 0%, #FFFFFF 55%)',
};

const props = defineProps({
  overlineText: { type: String, default: 'CENTRUM MEDYCZNE CODE' },
  headingText: { type: String, default: 'Nasze kliniki i instytuty' },
  plan1Title: { type: String, default: 'Klinika Przyczynowego Leczenia Niepłodności' },
  plan1Description: { type: String, default: '' },
  plan1ButtonLabel: { type: String, default: 'Przejdź do kliniki' },
  infertilityClinicUrl: { type: String, default: '#' },
  plan2Title: { type: String, default: 'Klinika Zdrowia Rodziny' },
  plan2Description: { type: String, default: '' },
  plan2ButtonLabel: { type: String, default: 'Przejdź do kliniki' },
  familyHealthClinicUrl: { type: String, default: '#' },
  plan3Title: { type: String, default: 'Instytut Genomiki i Medycyny Molekularnej' },
  plan3Description: { type: String, default: '' },
  plan3ButtonLabel: { type: String, default: 'Przejdź do instytutu' },
  genomicsInstituteUrl: { type: String, default: '#' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
});

const cards = computed(() => {
  const definitions = [
    {
      siteContext: CARD_SITE_CONTEXTS[0],
      title: props.plan1Title,
      description: props.plan1Description,
      buttonLabel: props.plan1ButtonLabel,
      buttonUrl: props.infertilityClinicUrl,
      gradient: CARD_GRADIENTS[CARD_SITE_CONTEXTS[0]],
    },
    {
      siteContext: CARD_SITE_CONTEXTS[1],
      title: props.plan2Title,
      description: props.plan2Description,
      buttonLabel: props.plan2ButtonLabel,
      buttonUrl: props.familyHealthClinicUrl,
      gradient: CARD_GRADIENTS[CARD_SITE_CONTEXTS[1]],
    },
    {
      siteContext: CARD_SITE_CONTEXTS[2],
      title: props.plan3Title,
      description: props.plan3Description,
      buttonLabel: props.plan3ButtonLabel,
      buttonUrl: props.genomicsInstituteUrl,
      gradient: CARD_GRADIENTS[CARD_SITE_CONTEXTS[2]],
    },
  ];

  return definitions;
});
</script>
